import React, { useRef, useState, useEffect, useCallback } from "react";
import "./Payment.css";
import KRGlue from "@lyracom/embedded-form-glue";
import {
  postCreatePaymentForm,
  getOnPaymentCallback,
  PAYMENT_GATEWAY_PUBLIC_KEY,
} from "../../core/api/Api";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { showToast } from "../../core/common-service/CommonService";
import useLangNavigate from "../common/useLangNavigate";
import { PAYMENT_GATEWAY_ENDPOINT } from "../../core/constants/constants";
import { trackCheckoutEvent } from "../../core/utils/trackCheckoutEvent";
import { getCurrencyForLocale, getLocaleFromPath, DEFAULT_LOCALE } from "../../config/locales";

const Payment = () => {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const navigate = useLangNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const activeLocale = getLocaleFromPath(window.location.pathname) ?? DEFAULT_LOCALE;
  const currency = getCurrencyForLocale(activeLocale);
  // let { service_session_id, plan_id } = useParams();
  const { service_session_id, plan_id, is_add_chapter } = location.state || {};

  const [isPaymentFormRendered, setIsPaymentFormRendered] = useState(false);
  const initiationAmountRef = useRef(null);

  const loadPaymentForm = useCallback(
    async (formToken, orderId) => {
      const endpoint = PAYMENT_GATEWAY_ENDPOINT;
      const publicKey = PAYMENT_GATEWAY_PUBLIC_KEY;

      const { KR } = await KRGlue.loadLibrary(
        endpoint,
        publicKey
      ); /* Load the remote library */

      await KR.setFormConfig({
        /* set the minimal configuration */
        formToken,
        "kr-language": i18n.language /* to update initialization parameter */,
      });

      await KR.renderElements(
        "#myPaymentForm"
      ); /* Render the payment form into myPaymentForm div */

      setIsPaymentFormRendered(true);

      await KR.onSubmit(async (res) => {
        const orderStatus = res.clientAnswer.orderStatus;
        if (orderStatus === "PAID") {
          getOnPaymentCallback(service_session_id)
            .then((res) => {
              const response = res?.data?.success;
              if (response) {
                showToast({ type: "success", message: response });

                trackCheckoutEvent({
                  eventType: "purchase",
                  transactionId: orderId || service_session_id,
                  value: initiationAmountRef.current ?? res?.data?.price ?? 0,
                  items: [
                    {
                      item_id: plan_id,
                      item_name: res?.data?.planName || `Plan - ${plan_id}`,
                      item_category: is_add_chapter
                        ? "Add Chapter"
                        : "Subscription",
                      price: initiationAmountRef.current ?? res?.data?.price ?? 0,
                      quantity: 1,
                    },
                  ],
                  output: "success",
                });

                navigate(
                  `/thankyou/purchase?order=${service_session_id}&plan=${plan_id}`
                );
              }
            })
            .catch((e) => {
              trackCheckoutEvent({
                eventType: "purchase",
                transactionId: orderId || service_session_id,
                value: initiationAmountRef.current ?? e?.response?.data?.price ?? 0,
                items: [
                  {
                    item_id: plan_id,
                    item_name: `Plan - ${plan_id}`,
                    item_category: is_add_chapter
                      ? "Add Chapter"
                      : "Subscription",
                    price: initiationAmountRef.current ?? e?.response?.data?.price ?? 0,
                    quantity: 1,
                  },
                ],
                output: "fail",
              });
            });
        }
        return false; // Return false to prevent the redirection
      });

      await KR.onError(async (res) => {
        const detailedErrorMessage = res.detailedErrorMessage;

        try {
          const resp = await getOnPaymentCallback(service_session_id);

          trackCheckoutEvent({
            eventType: "purchase",
            transactionId: orderId || service_session_id,
            value: initiationAmountRef.current ?? resp?.data?.price ?? 0,
            items: [
              {
                item_id: plan_id,
                item_name: `Plan - ${plan_id}`,
                item_category: is_add_chapter ? "Add Chapter" : "Subscription",
                price: initiationAmountRef.current ?? resp?.data?.price ?? 0,
                quantity: 1,
              },
            ],
            output: "fail",
          });
        } catch (error) {
          let price = 0;
          if (error?.response?.data?.price !== undefined) {
            price = error.response.data.price;
          }

          trackCheckoutEvent({
            eventType: "purchase",
            transactionId: orderId || service_session_id,
            value: initiationAmountRef.current ?? price,
            items: [
              {
                item_id: plan_id,
                item_name: `Plan - ${plan_id}`,
                item_category: is_add_chapter ? "Add Chapter" : "Subscription",
                price: initiationAmountRef.current ?? price,
                quantity: 1,
              },
            ],
            output: "fail",
          });
        }
        showToast({ type: "error", message: detailedErrorMessage });
      });
    },
    [i18n.language, navigate, plan_id, service_session_id, is_add_chapter]
  );
  const onInit = useRef(true);

  useEffect(() => {
    if (!onInit.current) return;
    onInit.current = false;
    window.scroll(0, 0);
    async function setupPaymentForm() {
      try {
        postCreatePaymentForm({
          service_session_id,
          plan_id,
          is_add_chapter,
        })
          .then(async (res) => {
            const response = res?.data?.success;
            if (response) {
              const formToken = response?.formToken;
              const orderId = response?.orderId || response?.order_id;
              initiationAmountRef.current = response?.final_amount ?? null;
              loadPaymentForm(formToken, orderId);
            }
          })
          .catch((e) => {});
      } catch (error) {
        navigate(-1);
        setMessage(error + " (see console for more details)");
      }
    }
    setupPaymentForm();
  }, [is_add_chapter, loadPaymentForm, navigate, plan_id, service_session_id]);

  return (
    <section
      className="aai-features py-100 payment"
    >
      <div className="form">
        <div className="d-flex align-items-center justify-content-center">
          <div id="myPaymentForm">
            <div className="kr-smart-form" kr-card-form-expanded="true"></div>
          </div>
          <div data-test="payment-message">{message}</div>
        </div>
        {isPaymentFormRendered && (
          <div className="centered-div">{t("for_more_info_contact_us")}</div>
        )}
      </div>
    </section>
  );
};

export default Payment;
